import React, { useContext } from 'react';
import { string } from 'prop-types';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import * as R from 'ramda';
import { format } from 'date-fns';

// Context
import { SequencingWizardContext } from '../SequencingWizardContext';

const Header = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.graphic.lighter,
  borderTop: `0.1em solid ${theme.palette.graphic.light}`,
  display: 'flex',
  justifyContent: 'space-between',
  minHeight: 48,
  padding: theme.spacing(0, 3),
  width: '100%',
  zIndex: 4,
}));

const HelperTextStyles = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.graphic.lighter,
  padding: theme.spacing(2, 3),
}));

const MultiRow = styled(Typography)(({ theme }) => ({
  '&:not(:last-of-type)': {
    borderBottom: `1px solid rgba(0, 0, 0, 0.1)`,
  },
  backgroundColor: theme.palette.graphic.lighter,
  padding: theme.spacing(2, 3),
}));

const Row = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.graphic.lighter,
  borderBottom: `1px solid rgba(0, 0, 0, 0.1)`,
  padding: theme.spacing(2, 3),
}));

const TreeSubtitle = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.graphic.light,
  borderTop: `1px solid rgba(0, 0, 0, 0.08)`,
  display: 'flex',
  height: 32,
  // Top padding necessary to center align text
  padding: theme.spacing(0.5, 3),
  width: '100%',
}));

const SummaryDrawer = props => {
  const { helperText, inventoryOwnerSubtitle } = props;

  const {
    data: { applyOnFutureDate, changeList, futureDate, inventoryOwners },
  } = useContext(SequencingWizardContext);

  const renderInventoryOwners = () => (
    <>
      <TreeSubtitle variant="subtitle1">
        {`${inventoryOwnerSubtitle}${inventoryOwners.length > 1 ? 's' : ''}`}
      </TreeSubtitle>
      {inventoryOwners.map(({ companyCustomerIdName: id, nameLong }) => (
        <MultiRow key={id} variant="body1">
          {`${id} ${nameLong}`}
        </MultiRow>
      ))}
    </>
  );

  const renderProductLocations = () => (
    <>
      <TreeSubtitle variant="subtitle1">Updating Sequences</TreeSubtitle>
      {changeList.map(({ location, product }, index) => (
        <Row
          color="textPrimary"
          // eslint-disable-next-line react/no-array-index-key
          key={`${location.nameShort}-${product.nameShort}-${index}`}
          variant="body1"
        >
          {product.nameShort} at {location.nameShort}
        </Row>
      ))}
    </>
  );

  return (
    <Box bgcolor="graphic.lighter" height="100%">
      <Header>
        <Typography
          sx={{
            color: 'primary.dark',
          }}
          variant="subtitle1"
        >
          Summary
        </Typography>
      </Header>
      {!R.isEmpty(helperText) && (
        <>
          <TreeSubtitle variant="subtitle1">Sequence Authorization</TreeSubtitle>
          <HelperTextStyles variant="body1">{helperText}</HelperTextStyles>
        </>
      )}
      {applyOnFutureDate === 'yes' && (
        <>
          <TreeSubtitle variant="subtitle1">Apply Sequence on Future Date</TreeSubtitle>
          <HelperTextStyles variant="body1">
            {format(new Date(futureDate), 'M/d/yyyy')}
          </HelperTextStyles>
        </>
      )}
      <Box width={1}>{renderInventoryOwners()}</Box>
      <Box width={1}>{renderProductLocations()}</Box>
    </Box>
  );
};

SummaryDrawer.propTypes = {
  helperText: string,
  inventoryOwnerSubtitle: string.isRequired,
};

SummaryDrawer.defaultProps = {
  helperText: '',
};

export default SummaryDrawer;
